import React, { useState, useRef, useEffect } from "react";
import ReactCrop, { PixelCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import DialogLayout from "../dialog/DialogLayout";
import { useTranslation } from "react-i18next";
import DefaultAvatar from "./DefaultAvatar";

const MAX_SIZE = 10 * 1024; // 10KB

interface LogoUploadDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (base64: string) => Promise<void> | void;
  type: string;
  inline?: boolean;
}

const LogoUploadDialog: React.FC<LogoUploadDialogProps> = ({
  open,
  onClose,
  onSave,
  type,
  inline,
}) => {
  const { t } = useTranslation();
  const [src, setSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState<PixelCrop>({
    unit: "px",
    width: 120,
    height: 120,
    x: 120,
    y: 120,
  });
  const [croppedImage, setCroppedImage] = useState<string | null>(null);
  const [error, setError] = useState<string>("");
  const [optimizationInfo, setOptimizationInfo] = useState<string>("");
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const imgRef = useRef<HTMLImageElement | null>(null);

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (!open) {
      // Reset all state when dialog closes
      setSrc(null);
      setCroppedImage(null);
      setError("");
      setOptimizationInfo("");
      setIsSaving(false);
      setCrop({
        unit: "px",
        width: 120,
        height: 120,
        x: 120,
        y: 120,
      });
      imgRef.current = null;
    }
  }, [open]);

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (ev) => {
        setSrc(ev.target?.result as string);
        setOptimizationInfo("");
        setError("");
      };
      reader.readAsDataURL(file);
    }
  };

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      const reader = new FileReader();
      reader.onload = (ev) => {
        setSrc(ev.target?.result as string);
        setOptimizationInfo("");
        setError("");
      };
      reader.readAsDataURL(file);
    }
  };

  const onImageLoaded = (img: HTMLImageElement) => {
    imgRef.current = img;
    const displayWidth = img.width;
    const displayHeight = img.height;
    const minSide = Math.max(60, Math.min(displayWidth, displayHeight) - 32);
    const x = Math.max(0, (displayWidth - minSide) / 2);
    const y = Math.max(0, (displayHeight - minSide) / 2);
    const defaultCrop = {
      unit: "px" as const,
      width: minSide,
      height: minSide,
      x,
      y,
    };
    setCrop(defaultCrop);
    // Use requestAnimationFrame to ensure image is fully loaded
    requestAnimationFrame(() => {
      makeClientCrop(defaultCrop);
    });
    return false;
  };

  const makeClientCrop = async (crop: PixelCrop) => {
    if (!imgRef.current || !crop.width || !crop.height) {
      setError(t("Failed to process image. Please try again."));
      return;
    }

    try {
      const result = await getCroppedImg(imgRef.current, crop);
      if (result.base64) {
        setCroppedImage(result.base64);
        setOptimizationInfo(result.optimizationInfo);
        if (result.base64.length > MAX_SIZE * 1.37) {
          setError(
            t(
              "The cropped image still exceeds 10KB after optimization. Please try a different image or crop area."
            )
          );
        } else {
          setError("");
        }
      } else {
        setError(t("Failed to process image. Please try again."));
      }
    } catch (error) {
      console.error("Error cropping image:", error);
      setError(t("Failed to process image. Please try again."));
    }
  };

  function getCroppedImg(
    image: HTMLImageElement,
    crop: PixelCrop
  ): Promise<{ base64: string; optimizationInfo: string }> {
    return new Promise((resolve) => {
      let targetSize = 120;
      let quality = 0.92;
      let base64 = "";
      let originalSize = 120;
      let wasOptimized = false;

      function tryCompress() {
        const canvas = document.createElement("canvas");
        canvas.width = targetSize;
        canvas.height = targetSize;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          resolve({ base64: "", optimizationInfo: "" });
          return;
        }

        const scaleX = image.naturalWidth / image.width;
        const scaleY = image.naturalHeight / image.height;
        ctx.drawImage(
          image,
          crop.x * scaleX,
          crop.y * scaleY,
          crop.width * scaleX,
          crop.height * scaleY,
          0,
          0,
          targetSize,
          targetSize
        );

        base64 = canvas.toDataURL("image/png", quality);

        // Nếu ảnh vẫn quá lớn và chưa giảm kích thước xuống mức tối thiểu
        if (base64.length > MAX_SIZE * 1.37 && targetSize > 60) {
          // Giảm kích thước ảnh
          targetSize = Math.max(60, targetSize - 20);
          quality = 0.92; // Reset quality khi giảm kích thước
          wasOptimized = true;
          tryCompress();
        } else if (base64.length > MAX_SIZE * 1.37 && quality > 0.3) {
          // Nếu đã giảm kích thước tối thiểu, giảm chất lượng
          quality -= 0.05;
          wasOptimized = true;
          tryCompress();
        } else {
          let optimizationInfo = "";
          if (wasOptimized) {
            if (targetSize < originalSize) {
              optimizationInfo =
                t("Image optimized: size reduced to") + ` ${targetSize}x${targetSize}`;
            } else if (quality < 0.92) {
              optimizationInfo =
                t("Image optimized: quality reduced to") + ` ${Math.round(quality * 100)}%`;
            }
          }
          resolve({ base64, optimizationInfo });
        }
      }

      tryCompress();
    });
  }

  const handleSave = async () => {
    if (!croppedImage) {
      setError(t("Please wait for the image to be processed."));
      return;
    }

    if (croppedImage.length > MAX_SIZE * 1.37) {
      setError(
        t(
          "The image must be less than or equal to 10KB. Please try a different image or crop area."
        )
      );
      return;
    }

    setIsSaving(true);
    setError("");

    try {
      await onSave(croppedImage);
      // onSave should handle closing the dialog, but we ensure it happens
    } catch (error) {
      console.error("Error saving logo:", error);
      setError(t("Failed to save image. Please try again."));
      setIsSaving(false);
    }
  };

  return (
    <DialogLayout show={open} inline={inline} onClose={onClose}>
      <div className="flex flex-col items-center justify-center w-full max-w-sm mx-auto p-0 sm:p-6">
        <h2 className="text-xl font-bold mb-2 text-center mt-2">{t("Upload and resize image")}</h2>
        {!src && (
          <div
            className="w-84 h-84 border-2 border-dashed border-gray-300 rounded-xl bg-gray-50 flex flex-col items-center justify-center cursor-pointer transition hover:border-blue-400 mb-3"
            onDrop={onDrop}
            onDragOver={(e) => e.preventDefault()}
            onClick={() => document.getElementById("logo-upload-input")?.click()}
          >
            <input
              id="logo-upload-input"
              type="file"
              accept="image/*"
              onChange={onFileChange}
              className="hidden"
            />
            <div className="text-5xl mb-2 flex items-center justify-center">
              <DefaultAvatar
                name={""}
                type={type as "COMPANY" | "CONTACT" | "SALE" | "TASK"}
                iconSize="4xl"
                className="mb-2"
              />
            </div>
            <div className="text-base font-medium text-gray-700 text-center">
              {t("Drop a file to upload, or click to select it.")}
            </div>
            <div className="text-xs text-gray-500 mt-1 text-center">
              {t("Max size: 10KB (auto-optimized)")}
            </div>
          </div>
        )}
        {src && (
          <div className="my-1 w-full flex flex-col items-center">
            <div className="bg-gray-100 rounded-xl w-84 h-84 flex flex-col items-center justify-center">
              <ReactCrop
                crop={crop}
                onChange={(newCrop) => setCrop(newCrop)}
                onComplete={makeClientCrop}
                aspect={1}
                circularCrop
              >
                <img
                  src={src}
                  onLoad={(e) => onImageLoaded(e.currentTarget as HTMLImageElement)}
                  alt="logo"
                  className="max-w-full max-h-full rounded-lg bg-white"
                />
              </ReactCrop>
            </div>
            {croppedImage && (
              <div className="flex flex-col items-center mt-2">
                <img
                  src={croppedImage}
                  alt="Cropped"
                  style={{ maxWidth: 64, maxHeight: 64, borderRadius: "50%" }}
                />
                <div className="text-xs text-gray-500 mt-0.5">
                  {(croppedImage.length / 1.37 / 1024).toFixed(2)} KB
                </div>
                {optimizationInfo && (
                  <div className="text-xs text-blue-600 mt-0.5 text-center">{optimizationInfo}</div>
                )}
              </div>
            )}
          </div>
        )}
        {error && <div className="text-red-600 text-xs mt-1 text-center">{error}</div>}
        <div className="flex flex-row justify-center gap-2 mt-4 w-full">
          <button
            className="px-4 py-2 rounded bg-blue-600 text-white text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            onClick={handleSave}
            disabled={!croppedImage || isSaving}
          >
            {isSaving && (
              <svg
                className="animate-spin h-4 w-4 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
            )}
            {t("Update Image")}
          </button>
          <button
            className="px-4 py-2 rounded bg-gray-200 text-gray-800 text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={onClose}
            disabled={isSaving}
          >
            {t("Cancel")}
          </button>
        </div>
      </div>
    </DialogLayout>
  );
};

export default LogoUploadDialog;
